import { Breadcrumb, HeaderToolbar, Skeleton } from "@xola/ui-kit";
import {
    isGiftOfferProduct,
    isMembershipProduct,
    isMerchandiseProduct,
    isPackage,
    isPackageProduct,
    isPartnerPackageProduct,
} from "common";
import { floor, isNumber } from "lodash";
import React, { useCallback, useEffect } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { Experience, GiftOffer, Merchandise, Package, PartnerPackage, Product } from "types";
import { GiftQrCodeScanner } from "../components/Common/GiftQrCodeScanner";
import { ColumnLayout } from "../components/Layout/ColumnLayout";
import { Loader } from "../components/Loader";
import { getTerms } from "../components/Product/EditGiftOffer";
import { CheckoutPurchaseScreen } from "../components/PurchaseCreate/CheckoutPurchaseScreen";
import { CreatePurchaseExperienceScreen } from "../components/PurchaseCreate/CreatePurchaseExperienceScreen";
import { CreatePurchaseGiftScreen } from "../components/PurchaseCreate/CreatePurchaseGiftScreen";
import { WaitlistSuccessScreen } from "../components/PurchaseCreate/WaitlistSuccessScreen";
import { useSeller } from "../hooks/useAccount";
import { useQueryParams } from "../hooks/useQueryParams";
import { makeGiftOfferItem, makePackagePurchaseItem } from "../models/CreatePurchaseState";
import { isVariableDemographicsPackage } from "../models/Package";
import { CreatePurchaseStep, useCreatePurchaseStore } from "../stores/createPurchaseStore";
import { Page } from "./Page";
import { CheckoutGiftPurchaseScreen } from "@/components/PurchaseCreate/CheckoutGiftPurchaseScreen";
import { PartnerPackagePurchase } from "@/components/PurchaseCreate/CreatePartnerPackagePurchase";
import { CreatePurchaseMembershipScreen } from "@/components/PurchaseCreate/Membership/CreatePurchaseMembershipScreen";
import { CheckoutMerchandisePurchaseScreen } from "@/components/PurchaseCreate/Merchandise/CheckoutMerchandisePurchaseScreen";
import { CreatePurchaseMerchandiseScreen } from "@/components/PurchaseCreate/Merchandise/CreatePurchaseMerchandiseScreen";
import { usePurchaseResponseStore } from "@/components/PurchaseCreate/store/usePurchaseResponseStore";
import { useExperiences, useProduct } from "@/hooks/resources/useProduct";

export const EditPurchasePage = () => {
    const itemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);
    const resetPurchaseResponseState = usePurchaseResponseStore((state) => state.actions.resetState);
    const getSeller = useCreatePurchaseStore((state) => state.getSeller);
    const { addItem, resetState, initializeExperienceItem, initializeMerchandiseItem, setSeller } =
        useCreatePurchaseStore((state) => state.actions);
    const productId = useCreatePurchaseStore(
        (state) => state.purchase.items[itemIndex]?.parentProduct?.id ?? state.purchase.items[itemIndex]?.product.id,
    );
    const isPurchaseEmpty = useCreatePurchaseStore((state) => state.purchase.items.length === 0);
    const history = useHistory();
    const queryParams = useQueryParams();
    const seller = useSeller();
    const giftCode = queryParams.get("giftCode") ?? undefined;
    const giftValue = queryParams.get("giftValue")
        ? floor(Number.parseFloat(queryParams.get("giftValue") ?? "0"), 2)
        : undefined;

    const { data: experiences } = useExperiences();
    const handleProductLoaded = (product: Product) => {
        const hasGiftCard = giftCode && isNumber(giftValue);
        if (queryParams.get("product")) {
            // We have query params, so we must have opened the app and this page directly
            // Let's initialize the purchase with the given arrival date and time (if present in the URL)
            if (!hasGiftCard) {
                // Added condition to reset State only if there is no gift card since on scanning gift card,
                // we should append the item to cart instead of resetting it.
                resetState();

                resetPurchaseResponseState();
            }

            const date = queryParams.get("date");
            const time = queryParams.get("time");
            const arrival = date ? { date, time: time ? Number(time) : null } : null;
            const purchaseSeller = getSeller();
            if (!purchaseSeller?.id) {
                setSeller(product.seller.id);
            }

            if (isPackageProduct(product) || isPartnerPackageProduct(product)) {
                const packageData = makePackagePurchaseItem(product, experiences, arrival);
                for (const itemData of packageData) {
                    addItem(itemData);
                }
            } else if (isGiftOfferProduct(product)) {
                const giftOfferData = {
                    giftOffer: product,
                    giftPreference: seller.preferences.gift!,
                    giftCode,
                    giftValue,
                    giftOfferTerms: getTerms(product),
                };
                addItem(makeGiftOfferItem(giftOfferData));
            } else if (isMerchandiseProduct(product)) {
                initializeMerchandiseItem(product as Merchandise);
            } else {
                initializeExperienceItem(product as Experience);
            }

            if (hasGiftCard) {
                // Removing GiftCode & GiftValue from Query params
                history.replace("/cart");
            }
        }
    };

    const { data: product, isLoading } = useProduct<Experience | GiftOffer | Package | PartnerPackage>(
        queryParams.get("product") ?? productId,
    );

    useEffect(() => {
        if (product) {
            handleProductLoaded(product);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product, giftCode, giftValue]);

    if (isPurchaseEmpty && !queryParams.get("product")) {
        return <Redirect to="/cart/init" />;
    }

    return (
        <Page isDynamicTitle={!!product} title={product ? `Make a Purchase for ${product.name}` : "Make a Purchase"}>
            <GiftQrCodeScanner />
            <ColumnLayout>
                <HeaderToolbar>
                    <HeaderToolbar.Breadcrumb className="border-r-0">
                        <Breadcrumb.Item>Purchases</Breadcrumb.Item>
                        <Breadcrumb.Item>Make a Purchase</Breadcrumb.Item>
                    </HeaderToolbar.Breadcrumb>
                </HeaderToolbar>

                <ColumnLayout.Fill>
                    {isLoading || isPurchaseEmpty ? <PurchaseSkeleton /> : <DisplayPage product={product!} />}
                </ColumnLayout.Fill>
            </ColumnLayout>
        </Page>
    );
};

const PurchaseSkeleton = () => {
    return (
        <div className="grid grid-cols-5 gap-8 p-8">
            <div className="col-span-3">
                <Skeleton height={800} />
            </div>

            <div className="col-span-2">
                <Skeleton height={800} />
            </div>
        </div>
    );
};

interface DisplayPageProps {
    product: Experience | GiftOffer | Package | PartnerPackage | Merchandise;
}

const DisplayPage = React.memo(({ product }: DisplayPageProps) => {
    const history = useHistory();
    const itemExists = useCreatePurchaseStore((state) => !!state.purchase.items[state.activeItemIndex]);
    const experience = useCreatePurchaseStore((state) => state.getActiveProduct<Experience>());
    const step = useCreatePurchaseStore((state) => state.step);
    const isMist = useCreatePurchaseStore((state) => state.purchase.items.length > 1);
    const isFirstScreen = [CreatePurchaseStep.SELECT_DATE, CreatePurchaseStep.GIFT_DETAILS_INVALID].includes(step);
    const isVariesDemographicPackage = isVariableDemographicsPackage(product);
    const showPackageCheckout = isPackage(product) && isFirstScreen;

    const toggleCheckout = useCallback(() => {
        useCreatePurchaseStore.setState({
            step: isFirstScreen ? CreatePurchaseStep.CHECKOUT : CreatePurchaseStep.SELECT_DATE,
            isAllProductDetailsUpdated: true,
        });
    }, [isFirstScreen]);

    if (isPartnerPackageProduct(product) && isVariesDemographicPackage) {
        <PartnerPackagePurchase product={product} toggleCheckout={toggleCheckout} />;
    }

    if (isVariesDemographicPackage) {
        if (step === CreatePurchaseStep.CHECKOUT) {
            return <CheckoutPurchaseScreen product={product as Package} onBack={toggleCheckout} />;
        }

        return <CreatePurchaseExperienceScreen experience={experience} onSubmit={toggleCheckout} />;
    }

    /*
     * The condition below is added to check if an item exists in the case
     * where the user clicks on "pay" on the checkout page.
     * Before redirecting to another page, we reset the state and set the step to SELECT_DATE,
     * using the default state where the item is undefined.
     * This results in a TypeError.
     * This error is described in ticket X2-7214.
     */

    if (showPackageCheckout) {
        return <CheckoutPurchaseScreen product={product as Package} onBack={() => history.push("/cart/init")} />;
    }

    if (isMist && isFirstScreen) {
        if (!itemExists) {
            return <Loader />;
        }

        return isGiftOfferProduct(product) ? (
            <CreatePurchaseGiftScreen product={product} onSubmit={toggleCheckout} />
        ) : isMembershipProduct(product) ? (
            <CreatePurchaseMembershipScreen membership={product} onSubmit={toggleCheckout} />
        ) : isMerchandiseProduct(product) ? (
            <CreatePurchaseMerchandiseScreen merchandise={product} onSubmit={toggleCheckout} />
        ) : (
            <CreatePurchaseExperienceScreen
                experience={isVariesDemographicPackage ? experience : (product as Experience)}
                onSubmit={toggleCheckout}
            />
        );
    }

    if (step === CreatePurchaseStep.WAITLIST_SUCCESS) {
        return <WaitlistSuccessScreen />;
    }

    if (!isMist && isGiftOfferProduct(product)) {
        return <CheckoutGiftPurchaseScreen product={product} onBack={toggleCheckout} />;
    }

    if (!isMist && isMerchandiseProduct(product)) {
        return <CheckoutMerchandisePurchaseScreen merchandise={product} onBack={() => history.push("/cart/init")} />;
    }

    return <CheckoutPurchaseScreen onBack={toggleCheckout} />;
});
